// Die Geräte der Firma (PLAN.md, Abschnitt 3; neu gestaltet in E3, S7 nach
// Grundsatz 1): jedes Gerät, auf dem sich jemand angemeldet hat – je App ein
// Eintrag. Ein neues Gerät wartet, bis es jemand freigibt; es steht dann
// ganz oben auf der Seite, nicht zu übersehen („wartet auf dich“). Sperren
// wirkt sofort – das Gerät kommt nicht mehr hinein, bis es wieder
// freigegeben ist; dann fragt es neu nach Face ID oder Fingerabdruck. Das
// Gerät, an dem man gerade sitzt, sperrt man nicht selbst.
//
// Hier stehen nur die Bausteine; die Seite (Leute.tsx) lädt, fragt nach
// und zeigt, was schiefging.

import type { GeraetZeile } from "@happy-energy/kern";
import { Chip, Ikon } from "@happy-energy/oberflaeche";
import { wannZeigen } from "./anzeige.ts";
import { geraetSperrbar } from "./leute/rollen.ts";

export const APP_NAME: Record<GeraetZeile["app"], string> = { doku: "Doku-App", crm: "CRM" };

function StandChip({ g }: { g: GeraetZeile }) {
  if (g.stand === "wartet")
    return (
      <Chip art="warn" ikon="uhr">
        wartet auf dich
      </Chip>
    );
  if (g.stand === "gesperrt")
    return (
      <Chip art="fehler" ikon="schloss">
        gesperrt
      </Chip>
    );
  return (
    <Chip art="ok" ikon="haken">
      freigegeben
    </Chip>
  );
}

export interface GeraetHandlungen {
  /** Darf ich die Geräte dieser Person ändern? (Die der Leitung ändert nur die Leitung.) */
  darf: (g: GeraetZeile) => boolean;
  freigeben: (g: GeraetZeile) => void;
  sperren: (g: GeraetZeile) => void;
  /** Was gerade läuft – der Knopf dreht sich. */
  beschaeftigt: string | null;
  /** Was an diesem Gerät schiefging. */
  meldung: (g: GeraetZeile) => string | undefined;
}

/** Ein Gerät in einer Zeile: Zeichen, Name, App, Person, zuletzt gesehen, Stand und die Knöpfe. */
export function GeraetEintrag({ g, ichGeraetId, h, auffaellig = false }: { g: GeraetZeile; ichGeraetId: string; h: GeraetHandlungen; auffaellig?: boolean }) {
  const dieses = g.id === ichGeraetId;
  const darf = h.darf(g);
  const laeuft = h.beschaeftigt === g.id;
  const meldung = h.meldung(g);
  const wann = g.stand === "wartet" ? `angemeldet ${wannZeigen(g.angelegtAm)}` : g.zuletztGesehen ? `zuletzt gesehen ${wannZeigen(g.zuletztGesehen)}` : "noch nie gesehen";
  return (
    <li className={`geraet-eintrag ${g.stand}${dieses ? " dieses" : ""}`}>
      <span className="geraet-zeichen">
        <Ikon name={g.app === "doku" ? "handy" : "laptop"} />
      </span>
      <div className="geraet-wort">
        <b>
          {g.name}
          {dieses && (
            <Chip art="lime" ikon="haken">
              dieses Gerät
            </Chip>
          )}
        </b>
        <span>
          {APP_NAME[g.app]} · {g.nutzer.name} · {wann}
        </span>
      </div>
      <div className="geraet-rechts">
        <StandChip g={g} />
        {darf && g.stand !== "freigegeben" && (
          <button type="button" className={`crm-knopf ${auffaellig ? "lime" : "rand"} klein`} onClick={() => h.freigeben(g)} disabled={h.beschaeftigt !== null}>
            {laeuft ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="haken" groesse="k" />} Freigeben
          </button>
        )}
        {darf && geraetSperrbar(ichGeraetId, g) && (
          <button type="button" className={`crm-knopf ${g.stand === "wartet" ? "leise" : "rand"} klein`} onClick={() => h.sperren(g)} disabled={h.beschaeftigt !== null}>
            <Ikon name="schloss" groesse="k" /> Sperren
          </button>
        )}
        {dieses && <span className="leise klein">hier sitzt du gerade</span>}
        {!darf && !dieses && <span className="leise klein">nur die Leitung</span>}
      </div>
      {meldung && (
        <p className="crm-fehler-satz geraet-meldung" role="alert">
          {meldung}
        </p>
      )}
    </li>
  );
}

/** Ganz oben: Geräte, die auf eine Freigabe warten – auffällig, mit „Freigeben“ in Lime. */
export function WartendeGeraete({ geraete, ichGeraetId, h }: { geraete: readonly GeraetZeile[]; ichGeraetId: string; h: GeraetHandlungen }) {
  if (geraete.length === 0) return null;
  return (
    <section className="wartet-band" aria-label="Geräte, die warten">
      <div className="wartet-kopf">
        <Ikon name="uhr" groesse="g" />
        <div>
          <b>{geraete.length === 1 ? "1 neues Gerät wartet auf dich" : `${geraete.length} neue Geräte warten auf dich`}</b>
          <span>Erst nach deiner Freigabe kommt es hinein. Kennst du es nicht, sperren.</span>
        </div>
      </div>
      <ul className="geraete-liste">
        {geraete.map((g) => (
          <GeraetEintrag key={g.id} g={g} ichGeraetId={ichGeraetId} h={h} auffaellig />
        ))}
      </ul>
    </section>
  );
}

/** Alle übrigen Geräte – freigegebene zuerst, gesperrte zuletzt. */
export function GeraeteBereich({ geraete, ichGeraetId, h }: { geraete: readonly GeraetZeile[]; ichGeraetId: string; h: GeraetHandlungen }) {
  return (
    <section className="crm-karte geraete-bereich" id="geraete" aria-label="Die Geräte">
      <div className="karten-kopf">
        <Ikon name="schloss" />
        <h2>Die Geräte</h2>
        <span className="leise klein">je App ein Eintrag – Sperren wirkt sofort</span>
      </div>
      {geraete.length === 0 ? (
        <p className="leise klein">Noch keine weiteren Geräte.</p>
      ) : (
        <ul className="geraete-liste">
          {geraete.map((g) => (
            <GeraetEintrag key={g.id} g={g} ichGeraetId={ichGeraetId} h={h} />
          ))}
        </ul>
      )}
    </section>
  );
}
